/**
 * Tree layer module.
 *
 * Renders trees as GPU-accelerated points with clustering at low zoom.
 * At zoom < 14: shows clusters with counts.
 * At zoom >= 14: shows individual tree health-colored dots.
 * Tree codes shown only on hover or selection.
 */

import type { Map } from 'mapbox-gl';
import type { MapTree } from '../types/mapTypes';
import { treeToFeature, featureCollection } from '../utils/geojson';

const SOURCE_ID = 'trees-source';
const CLUSTER_LAYER_ID = 'tree-clusters';
const CLUSTER_COUNT_LAYER_ID = 'tree-cluster-count';
const POINT_LAYER_ID = 'tree-points';

export const TREE_COLORS: Record<string, string> = {
  excellent: '#059669',
  good: '#16A34A',
  watch: '#D97706',
  at_risk: '#F97316',
  critical: '#DC2626',
};

function healthColor(status: string): string {
  return TREE_COLORS[status] || '#6B7280';
}

export function addTreeLayers(map: Map): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: featureCollection([]),
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius: 50,
    clusterProperties: {
      // Keep track of issues count per cluster
      totalIssues: ['+', ['coalesce', ['get', 'openIssueCount'], 0]],
    },
  });

  // Cluster circles
  map.addLayer({
    id: CLUSTER_LAYER_ID,
    type: 'circle',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'case',
        ['>', ['get', 'totalIssues'], 0],
        '#FCA5A5', // red tint if cluster has issues
        '#BBF7D0', // green tint otherwise
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        18,
        100,
        24,
        500,
        30,
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
    },
  });

  // Cluster count labels
  map.addLayer({
    id: CLUSTER_COUNT_LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-size': 12,
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
    },
    paint: {
      'text-color': '#166534',
    },
  });

  // Individual tree points
  map.addLayer({
    id: POINT_LAYER_ID,
    type: 'circle',
    source: SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#2563EB',
        ['case',
          ['>', ['coalesce', ['get', 'openIssueCount'], 0], 0],
          '#DC2626',
          ['coalesce', ['get', '_healthColor'], '#16A34A'],
        ],
      ],
      'circle-radius': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        8,
        4.5,
      ],
      'circle-stroke-width': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        3,
        1.5,
      ],
      'circle-stroke-color': '#FFFFFF',
    },
  });
}

export function updateTreeData(map: Map, trees: MapTree[]): void {
  const source = map.getSource(SOURCE_ID);
  if (!source || source.type !== 'geojson') return;

  const features = trees.map((tree) => {
    const feature = treeToFeature(tree);
    // Add health color as a property for styling
    (feature.properties as any)._healthColor = healthColor(tree.healthStatus);
    return feature;
  });

  (source as any).setData(featureCollection(features));
}

export function removeTreeLayers(map: Map): void {
  if (map.getLayer(POINT_LAYER_ID)) map.removeLayer(POINT_LAYER_ID);
  if (map.getLayer(CLUSTER_COUNT_LAYER_ID)) map.removeLayer(CLUSTER_COUNT_LAYER_ID);
  if (map.getLayer(CLUSTER_LAYER_ID)) map.removeLayer(CLUSTER_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

export function zoomIntoCluster(
  map: Map,
  clusterId: number,
): void {
  const source = map.getSource(SOURCE_ID);
  if (!source || source.type !== 'geojson') return;

  (source as any).getClusterLeaves(
    clusterId,
    1000,
    0,
    (err: any, features: any[]) => {
      if (err || !features.length) return;

      let minLng = Infinity,
        maxLng = -Infinity,
        minLat = Infinity,
        maxLat = -Infinity;

      for (const f of features) {
        const [lng, lat] = f.geometry.coordinates;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }

      map.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: 50, maxZoom: 16 },
      );
    },
  );
}

export function setTreeSelected(map: Map, treeId: number | null): void {
  if (treeId != null) {
    map.setFeatureState(
      { source: SOURCE_ID, id: treeId },
      { selected: true },
    );
  }
}

export { SOURCE_ID as treeSourceId, POINT_LAYER_ID as treePointLayerId, CLUSTER_LAYER_ID as treeClusterLayerId };
